
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { normalizeSessionFormats, normalizeSessionFormat } from '@/utils/session-format-helpers';

export interface Practitioner {
  id: string;
  first_name: string | null;
  last_name: string | null;
  professional_title: string | null;
  bio: string | null;
  avatar_url: string | null;
  languages_spoken: string[] | null;
  session_formats_offered: string[] | null;
  in_person_address: string | null;
  latitude: number | null;
  longitude: number | null;
  average_rating: number | null;
  total_reviews: number;
  accepting_new_clients: boolean;
  is_verified: boolean;
  profile_status: string;
  years_of_experience: number | null;
  country: string | null;
  timezone: string;
  gender: string | null;
  slug: string | null;
  created_at?: string;
  // Third-party rating fields
  third_party_order_count?: number | null;
  third_party_review_count?: number | null;
  third_party_rating?: number | null;
  third_party_rating_verified?: boolean;
  third_party_platform_name?: string | null;
  services?: any[];
  categories?: any[];
  distance?: number;
  practitioner_service_categories?: any[];
}

interface FilterOptions {
  serviceCategories: string[];
  effectiveDomain?: string;
  serviceSubcategory?: string;
  distance: string;
  date: Date | undefined;
  timeSlot: string;
  sessionType: string | string[];
  genders: string[];
  language: string;
  issueCategory?: string;
  showFreeSession: boolean;
  includeThirdPartyRating?: boolean;
  userLocation?: { lat: number; lng: number } | null;
  searchQuery?: string;
}

const PRACTITIONERS_PER_PAGE = 12;

export const usePractitioners = (filters: FilterOptions, sortBy: string = 'rating') => {
  const [practitioners, setPractitioners] = useState<Practitioner[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);

  const fetchPractitioners = useCallback(async (pageNum = 0, reset = true) => {
    console.log('🔍 fetchPractitioners called with:', { pageNum, reset, filters, sortBy });
    try {
      if (reset) {
        setIsLoading(true);
        setError(null);
      }

      // Build base query with pagination - fetch from practitioners table directly with third-party fields
      const pageStart = pageNum * PRACTITIONERS_PER_PAGE;
      const pageEnd = (pageNum + 1) * PRACTITIONERS_PER_PAGE - 1;
      let query = supabase
        .from('practitioners')
        .select(`
          id, first_name, last_name, professional_title, bio, avatar_url,
          languages_spoken, session_formats_offered, latitude, longitude,
          average_rating, total_reviews, years_of_experience, country, slug,
          accepting_new_clients, third_party_order_count, third_party_review_count,
          third_party_rating, third_party_rating_verified, third_party_platform_name,
          number_of_completed_orders, created_at
        `)
        .eq('profile_status', 'approved')
        .eq('is_verified', true)
        .eq('accepting_new_clients', true);
      
      // Apply pagination
      const { data: basePractitioners, error: queryError } = await query.range(pageStart, pageEnd);

      if (queryError) {
        throw queryError;
      }

      // Fetch related services and categories separately (embedding is not available on views)
      const practitionerIds = (basePractitioners || []).map((p: any) => p.id);

      // If no practitioners are returned, short-circuit to avoid empty `.in()` queries
      if (!practitionerIds || practitionerIds.length === 0) {
        console.log("No practitioners found for current filters.");
        if (reset) {
          setPractitioners([]);
        } else {
          setPractitioners(prev => prev);
        }
        setHasMore(false);
        setPage(pageNum);
        setIsLoading(false);
        return;
      }

      const [servicesRes, categoriesRes] = await Promise.all([
        supabase
          .from('services')
          .select(`
            *,
            service_therapy_tags(
              id,
              is_primary,
              therapy_modality:therapy_modalities(
                id,
                name_cn,
                name_en,
                type,
                evidence_level
              )
            )
          `)
          .in('practitioner_id', practitionerIds)
          .eq('is_active', true),
        supabase
          .from('practitioner_service_categories')
          .select('*, service_categories(*)')
          .in('practitioner_id', practitionerIds)
      ]);

      if (servicesRes.error) throw servicesRes.error;
      if (categoriesRes.error) throw categoriesRes.error;

      const servicesByPractitioner = new Map<string, any[]>();
      (servicesRes.data || []).forEach((s: any) => {
        const arr = servicesByPractitioner.get(s.practitioner_id) || [];
        arr.push(s);
        servicesByPractitioner.set(s.practitioner_id, arr);
      });

      const categoriesByPractitioner = new Map<string, any[]>();
      (categoriesRes.data || []).forEach((c: any) => {
        const arr = categoriesByPractitioner.get(c.practitioner_id) || [];
        arr.push(c);
        categoriesByPractitioner.set(c.practitioner_id, arr);
      });

      console.log("Raw practitioner data:", basePractitioners);

      let filteredPractitioners: Practitioner[] = (basePractitioners || []).map((practitioner: any) => {
        const svc = servicesByPractitioner.get(practitioner.id) || [];
        const normalizedServices = Array.isArray(svc)
          ? svc.map((s: any) => ({
              ...s,
              formats_available: normalizeSessionFormats(s.formats_available || []),
              therapy_tags: s.service_therapy_tags || []
            }))
          : svc;
        const serviceFormatsUnion = Array.from(
          new Set(
            (normalizedServices as any[]).flatMap((s: any) => s.formats_available || [])
          )
        );
        const cats = categoriesByPractitioner.get(practitioner.id) || [];

        return {
          ...practitioner,
          // Override practitioner-level formats with union of active services
          session_formats_offered: serviceFormatsUnion,
          services: normalizedServices,
          practitioner_service_categories: cats,
          distance: undefined // Initialize distance property
        } as Practitioner;
      });

      // Filter out practitioners with no active services
      filteredPractitioners = filteredPractitioners.filter(practitioner =>
        practitioner.services && practitioner.services.length > 0
      );

      // Apply enhanced search filter (name, title, bio, therapy tags, service name/description)
      if (filters.searchQuery && filters.searchQuery.trim()) {
        const searchTerm = filters.searchQuery.toLowerCase().trim();
        filteredPractitioners = filteredPractitioners.filter(practitioner => {
          const firstName = practitioner.first_name?.toLowerCase() || '';
          const lastName = practitioner.last_name?.toLowerCase() || '';
          const professionalTitle = practitioner.professional_title?.toLowerCase() || '';
          const fullName = `${firstName} ${lastName}`.trim();
          const bio = practitioner.bio?.toLowerCase() || '';
          
          // Check name and title match
          const nameMatch = firstName.includes(searchTerm) ||
                 lastName.includes(searchTerm) ||
                 fullName.includes(searchTerm) ||
                 professionalTitle.includes(searchTerm);
          
          // Check bio match
          const bioMatch = bio.includes(searchTerm);
          
          // Check therapy tags match
          const therapyMatch = practitioner.services?.some((service: any) =>
            service.therapy_tags?.some((tag: any) => {
              const therapyNameCn = tag.therapy_modality?.name_cn?.toLowerCase() || '';
              const therapyNameEn = tag.therapy_modality?.name_en?.toLowerCase() || '';
              return therapyNameCn.includes(searchTerm) || therapyNameEn.includes(searchTerm);
            })
          );
          
          // Check service name and description match
          const serviceMatch = practitioner.services?.some((service: any) => {
            const serviceName = service.name?.toLowerCase() || '';
            const serviceDescription = service.description?.toLowerCase() || '';
            return serviceName.includes(searchTerm) || serviceDescription.includes(searchTerm);
          });
          
          return nameMatch || bioMatch || therapyMatch || serviceMatch;
        });
        
        console.log(`Filtered practitioners by search "${filters.searchQuery}":`, filteredPractitioners.length);
      }

      // Apply therapy-based filtering (Stage/Domain/Subcategory)
      const hasTherapyFilters = 
        (filters.serviceCategories && filters.serviceCategories.length > 0 && !filters.serviceCategories.includes("all_types")) ||
        (filters.effectiveDomain && filters.effectiveDomain !== 'all_domains') ||
        (filters.serviceSubcategory && filters.serviceSubcategory !== 'all_subcategories');

      if (hasTherapyFilters) {
        try {
          // Step 1: Build therapy modalities query based on filters
          let therapyQuery: any = supabase
            .from('therapy_modalities')
            .select('id')
            .eq('is_active', true);

          // Filter by Stage
          if (filters.serviceCategories && !filters.serviceCategories.includes("all_types")) {
            const stageNum = getStageNumber(filters.serviceCategories[0]);
            const stageColumn = `stage_${stageNum}`;
            therapyQuery = therapyQuery.eq(stageColumn, true);
          }

          // Filter by Domain
          if (filters.effectiveDomain && filters.effectiveDomain !== 'all_domains') {
            therapyQuery = therapyQuery.eq(filters.effectiveDomain, true);
          }

          // Filter by Subcategory
          if (filters.serviceSubcategory && filters.serviceSubcategory !== 'all_subcategories') {
            therapyQuery = therapyQuery.eq(filters.serviceSubcategory, true);
          }

          const { data: matchingTherapies, error: therapyError } = await therapyQuery;
          if (therapyError) throw therapyError;

          const therapyIds = matchingTherapies?.map(t => t.id) || [];

          if (therapyIds.length > 0) {
            // Step 2: Find services with these therapies
            const { data: matchingServiceTags, error: tagsError } = await supabase
              .from('service_therapy_tags')
              .select('service_id')
              .in('therapy_modality_id', therapyIds);

            if (tagsError) throw tagsError;

            // Graceful degradation: if no therapy tags exist, use practitioner_service_categories as fallback
            if (!matchingServiceTags || matchingServiceTags.length === 0) {
              console.warn('⚠️ No therapy tags found for matching therapies. Using practitioner_service_categories as fallback.');

              // Fallback: Filter by practitioner_service_categories
              if (filters.serviceCategories && !filters.serviceCategories.includes("all_types")) {
                const categoryName = filters.serviceCategories[0];
                filteredPractitioners = filteredPractitioners.filter(p => {
                  return p.practitioner_service_categories?.some((cat: any) =>
                    cat.service_categories?.name === categoryName
                  );
                });
                console.log(`Filtered by service category (${categoryName}). Remaining:`, filteredPractitioners.length);
              }
            } else {
              const serviceIds = [...new Set(matchingServiceTags.map(st => st.service_id))];

              // Step 3: Find practitioners with these services
              const { data: matchingServices, error: servicesError } = await supabase
                .from('services')
                .select('practitioner_id')
                .in('id', serviceIds)
                .eq('is_active', true);

              if (servicesError) throw servicesError;

              const matchingPractitionerIds = [...new Set(matchingServices?.map(s => s.practitioner_id) || [])];

              // Step 4: Filter practitioners
              filteredPractitioners = filteredPractitioners.filter(p =>
                matchingPractitionerIds.includes(p.id)
              );

              console.log(
                `Filtered by therapy mappings (Stage/Domain/Subcategory). Remaining:`,
                filteredPractitioners.length
              );
            }
          } else {
            // No matching therapies found, return empty results
            filteredPractitioners = [];
            console.log('No therapies match the selected filters');
          }
        } catch (error) {
          console.error('Error filtering by therapies:', error);
        }
      }

      // Apply session type filter - support both array and string for backward compatibility
      const sessionTypeArray = Array.isArray(filters.sessionType) ? filters.sessionType : [filters.sessionType];
      
      if (sessionTypeArray.length > 0 && !sessionTypeArray.includes('all')) {
        // Only filter if not both selected (both virtual and in_person)
        if (!(sessionTypeArray.length === 2 && 
              sessionTypeArray.includes('virtual') && 
              sessionTypeArray.includes('in_person'))) {
          filteredPractitioners = filteredPractitioners.filter((practitioner: Practitioner) => {
            return sessionTypeArray.some((type: string) => {
              const targetType = normalizeSessionFormat(type);
              return practitioner.session_formats_offered?.includes(targetType);
            });
          });
        }
        // If both are selected, show all practitioners (no filtering)
      }

      // Apply gender filter - only filter if not all three selected
      if (filters.genders && filters.genders.length > 0 && 
          !(filters.genders.length === 3 && 
            filters.genders.includes('male') && 
            filters.genders.includes('female') && 
            filters.genders.includes('other'))) {
        filteredPractitioners = filteredPractitioners.filter((practitioner: Practitioner) => {
          if (!practitioner.gender) return false;
          
          // Map 'other' filter to include non_binary and prefer_not_to_say
          if (filters.genders.includes('other')) {
            return filters.genders.includes(practitioner.gender) || 
                   ['non_binary', 'prefer_not_to_say', 'other'].includes(practitioner.gender);
          }
          
          return filters.genders.includes(practitioner.gender);
        });
        console.log(`Filtered by gender (${filters.genders.join(', ')}). Remaining:`, filteredPractitioners.length);
      }

      // Apply language filter
      if (filters.language && filters.language !== 'all') {
        filteredPractitioners = filteredPractitioners.filter((practitioner: Practitioner) => {
          if (!practitioner.languages_spoken || practitioner.languages_spoken.length === 0) return false;
          return practitioner.languages_spoken.includes(filters.language);
        });
        console.log(`Filtered by language (${filters.language}). Remaining:`, filteredPractitioners.length);
      }

      // Apply issue category filter - only filter if specific category selected
      if (filters.issueCategory && filters.issueCategory !== 'all_categories') {
        try {
          // Query practitioner_issue_tags to find practitioners with matching category
          const { data: matchingIssueTags, error: issueTagsError } = await supabase
            .from('practitioner_issue_tags')
            .select('practitioner_id, issue_tags!inner(category)')
            .eq('issue_tags.category', filters.issueCategory);

          if (issueTagsError) throw issueTagsError;

          if (matchingIssueTags && matchingIssueTags.length > 0) {
            const matchingPractitionerIds = [...new Set(matchingIssueTags.map((tag: any) => tag.practitioner_id))];
            filteredPractitioners = filteredPractitioners.filter(p =>
              matchingPractitionerIds.includes(p.id)
            );
            console.log(`Filtered by issue category (${filters.issueCategory}). Remaining:`, filteredPractitioners.length);
          } else {
            // No matching practitioners, return empty result
            filteredPractitioners = [];
            console.log('No practitioners found with selected issue category');
          }
        } catch (error) {
          console.error('Error filtering by issue category:', error);
        }
      }

      // Apply free session filter
      if (filters.showFreeSession) {
        filteredPractitioners = filteredPractitioners.filter((practitioner: Practitioner) =>
          practitioner.services?.some((service: any) => service.is_free_session === true && service.is_active === true)
        );
      }

      // Calculate distance if user location is available
      if (filters.userLocation) {
        filteredPractitioners = filteredPractitioners.map(practitioner => {
          if (practitioner.latitude && practitioner.longitude) {
            const distance = calculateDistance(
              filters.userLocation!.lat,
              filters.userLocation!.lng,
              practitioner.latitude,
              practitioner.longitude
            );
            return { ...practitioner, distance };
          }
          return practitioner;
        });
      }

      // Apply distance filter if user location is available
      if (filters.distance !== 'any_distance' && filters.userLocation) {
        const maxDistance = parseInt(filters.distance);
        filteredPractitioners = filteredPractitioners.filter(practitioner => {
          if (!practitioner.latitude || !practitioner.longitude) return false;
          return practitioner.distance !== undefined && practitioner.distance <= maxDistance;
        });
      }

      // Apply sorting
      filteredPractitioners = sortPractitioners(filteredPractitioners, sortBy);

      if (reset) {
        setPractitioners(filteredPractitioners);
      } else {
        setPractitioners(prev => [...prev, ...filteredPractitioners]);
      }
      
      setHasMore((basePractitioners?.length || 0) === PRACTITIONERS_PER_PAGE);
      setPage(pageNum);
    } catch (err) {
      console.error('Error fetching practitioners:', err);
      setError('Failed to load practitioners. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [filters, sortBy]);

  const loadMore = useCallback(() => {
    if (!isLoading && hasMore) {
      fetchPractitioners(page + 1, false);
    }
  }, [fetchPractitioners, page, isLoading, hasMore]);

  useEffect(() => {
    // Add debounce to prevent rapid requests when filters change
    const timeoutId = setTimeout(() => {
      fetchPractitioners(0, true);
    }, 150);

    return () => {
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.serviceCategories, filters.effectiveDomain, filters.serviceSubcategory, filters.distance, filters.date, filters.timeSlot, filters.sessionType, filters.showFreeSession, filters.includeThirdPartyRating, filters.userLocation, filters.searchQuery, sortBy]); // Depend on filter values, not fetchPractitioners

  return { practitioners, isLoading, error, hasMore, loadMore };
};

// Helper function to compare practitioners by name (first_name, then last_name)
function compareByName(a: Practitioner, b: Practitioner): number {
  const aFirstName = a.first_name?.toLowerCase() || '';
  const bFirstName = b.first_name?.toLowerCase() || '';
  
  if (aFirstName !== bFirstName) {
    return aFirstName.localeCompare(bFirstName);
  }
  
  // If first names are the same, compare last names
  const aLastName = a.last_name?.toLowerCase() || '';
  const bLastName = b.last_name?.toLowerCase() || '';
  
  return aLastName.localeCompare(bLastName);
}

// Helper function to sort practitioners with secondary sorting by name
function sortPractitioners(practitioners: Practitioner[], sortBy: string): Practitioner[] {
  const sorted = [...practitioners];
  
  switch (sortBy) {
    case 'rating':
      return sorted.sort((a, b) => {
        // Get the best rating for each practitioner (platform vs third-party)
        const aRating = getBestRating(a);
        const bRating = getBestRating(b);
        
        // Primary sort: highest rating first
        if (bRating !== aRating) {
          return bRating - aRating;
        }
        
        // Secondary sort: by name when ratings are equal
        return compareByName(a, b);
      });
      
    case 'price':
      return sorted.sort((a, b) => {
        // Get the lowest service price for each practitioner
        const aMinPrice = Math.min(...(a.services?.map(s => s.price) || [Infinity]));
        const bMinPrice = Math.min(...(b.services?.map(s => s.price) || [Infinity]));
        
        // Primary sort: lowest price first
        if (aMinPrice !== bMinPrice) {
          return aMinPrice - bMinPrice;
        }
        
        // Secondary sort: by name when prices are equal
        return compareByName(a, b);
      });
      
    case 'price_high':
      return sorted.sort((a, b) => {
        // Get the highest service price for each practitioner
        const aMaxPrice = Math.max(...(a.services?.map(s => s.price) || [0]));
        const bMaxPrice = Math.max(...(b.services?.map(s => s.price) || [0]));
        
        // Primary sort: highest price first
        if (bMaxPrice !== aMaxPrice) {
          return bMaxPrice - aMaxPrice;
        }
        
        // Secondary sort: by name when prices are equal
        return compareByName(a, b);
      });
      
    case 'orders':
      return sorted.sort((a, b) => {
        // Get the best order count (platform vs third-party)
        const aOrders = getBestOrderCount(a);
        const bOrders = getBestOrderCount(b);
        
        // Primary sort: most orders first
        if (bOrders !== aOrders) {
          return bOrders - aOrders;
        }
        
        // Secondary sort: by name when order counts are equal
        return compareByName(a, b);
      });
      
    case 'newest':
      return sorted.sort((a, b) => {
        const aDate = new Date(a.created_at || 0).getTime();
        const bDate = new Date(b.created_at || 0).getTime();
        
        // Primary sort: newest first
        if (bDate !== aDate) {
          return bDate - aDate;
        }
        
        // Secondary sort: by name when dates are equal
        return compareByName(a, b);
      });
      
    case 'distance':
      return sorted.sort((a, b) => {
        const aDistance = a.distance || Infinity;
        const bDistance = b.distance || Infinity;
        
        // Primary sort: nearest first
        if (aDistance !== bDistance) {
          return aDistance - bDistance;
        }
        
        // Secondary sort: by name when distances are equal
        return compareByName(a, b);
      });
      
    default:
      // Default to rating sort
      return sorted.sort((a, b) => {
        const aRating = getBestRating(a);
        const bRating = getBestRating(b);
        
        if (bRating !== aRating) {
          return bRating - aRating;
        }
        
        return compareByName(a, b);
      });
  }
}

// Helper function to map service category name to stage number
function getStageNumber(categoryName: string): number {
  const stageMap: Record<string, number> = {
    'crisis_intervention': 0,
    'harmonize_mind_body': 1,
    'analysis_investigation': 2,
    'transpersonal_dive': 3,
    'actualization_integration': 4
  };
  return stageMap[categoryName] ?? 0;
}

// Helper function to calculate distance between two coordinates using Haversine formula
function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3959; // Radius of the Earth in miles
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c; // Distance in miles
  return d;
}

function deg2rad(deg: number): number {
  return deg * (Math.PI / 180);
}

// Helper function to get the best rating between platform and third-party
function getBestRating(practitioner: Practitioner): number {
  const platformRating = practitioner.average_rating || 0;
  const thirdPartyRating = (practitioner.third_party_rating_verified && practitioner.third_party_rating) || 0;
  
  return Math.max(platformRating, thirdPartyRating);
}

// Helper function to get the best review count between platform and third-party
function getBestReviewCount(practitioner: Practitioner): number {
  const platformReviews = practitioner.total_reviews || 0;
  const thirdPartyReviews = (practitioner.third_party_rating_verified && practitioner.third_party_review_count) || 0;
  
  return Math.max(platformReviews, thirdPartyReviews);
}

// Helper function to get the best order count between platform and third-party
function getBestOrderCount(practitioner: Practitioner): number {
  const platformOrders = (practitioner as any).number_of_completed_orders || 0;
  const thirdPartyOrders = (practitioner.third_party_rating_verified && practitioner.third_party_order_count) || 0;
  
  return Math.max(platformOrders, thirdPartyOrders);
}
